import type { LearningExercise } from "../types";

// Berufsrecht — Patentanwaltsordnung (PAO)
// Pflicht-Prüfungsgebiet § 40 Abs. 2 Nr. 7 PatAnwAPrV
// Abdeckung: §§ 1–3 (Beruf, Tätigkeitsbereich), §§ 5–6 (Zulassung), § 26 (Kanzleipflicht),
// §§ 39–39a (Berufspflichten, Verschwiegenheit), §§ 40–42 (Sozietät, Haftpflicht),
// §§ 43 ff. (Berufsaufsicht, Rüge, berufsgerichtliches Verfahren),
// §§ 53 ff. (Patentanwaltskammer), Vergütung/RVG-Bezug.
// Quellen inline, pathId "deutsche-ausbildung", mode "ausbildung".
// IDs: da-pao-001 … da-pao-038.
// Fallkonstellation-Items (≥ 30 %): da-pao-011, -014, -017, -020, -022, -025, -027, -029,
//   -031, -033, -034, -036 (12 von 38 = 32 %)
// reviewStatus "draft" für alle Items — Freigabe ausschließlich durch question-reviewer.
export const ausbildungBerufsrecht: LearningExercise[] = [

  // ─── FLASHCARDS da-pao-001 … da-pao-008 ─────────────────────────────────

  {
    id: "da-pao-001",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "PAO § 1 — Stellung des Patentanwalts",
    title: "Patentanwalt als Organ der Rechtspflege",
    prompt:
      "Welche Doppelstellung nimmt der Patentanwalt nach § 1 PAO in der Rechtsordnung ein, und welche Konsequenz hat dies für seine Berufsausübung?",
    options: [],
    answers: [],
    explanation:
      "§ 1 PAO: Der Patentanwalt ist ein unabhängiges Organ der Rechtspflege. Diese Doppelstellung bedeutet: (1) Er ist unabhängig — weder Beamter noch weisungsgebundener Angestellter des Staates, sondern freier Beruf; er übt sein Amt ohne Bindung an Weisungen des Mandanten aus, soweit Pflichten der Rechtspflege entgegenstehen. (2) Er ist Organ der Rechtspflege — er trägt Mitverantwortung für die geordnete Rechtspflege und ist zur Wahrheit und Redlichkeit verpflichtet, auch gegenüber Gerichten und Behörden. Die Unabhängigkeit schützt ihn vor Mandantenweisungen, die gegen Berufspflichten verstoßen würden.",
    sourceIds: ["pao1"],
    sources: [
      { id: "pao1", label: "§ 1 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__1.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["berufsrecht", "patentanwaltsordnung", "organ-der-rechtspflege"],
    // REVIEW da-pao-001 (REVIEWED): § 1 PAO "Der Patentanwalt ist ein unabhängiges Organ der
    // Rechtspflege" — korrekt zitiert; Erläuterung der Doppelstellung (Unabhängigkeit +
    // Mitverantwortung Rechtspflege) sachlich richtig. Flashcard, Quelle vorhanden, kein Leak.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-002",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "PAO § 3 — Tätigkeitsbereich",
    title: "Enumerierter Tätigkeitsbereich des Patentanwalts (§ 3 PAO)",
    prompt:
      "§ 3 PAO zählt den Tätigkeitsbereich des Patentanwalts auf. Welche gewerblichen Schutzrechte werden dort explizit genannt, und welches Recht überrascht erfahrungsgemäß in der Prüfung?",
    options: [],
    answers: [],
    explanation:
      "§ 3 Abs. 2 PAO: Der Patentanwalt ist auf den Gebieten des gewerblichen Rechtsschutzes — insbesondere betreffend Patente, ergänzende Schutzzertifikate, Gebrauchsmuster, eingetragene Designs (Geschmacksmuster), Topographien von Halbleitererzeugnissen (Halbleiterschutzgesetz), Marken sowie Sortenschutzrechte — tätig. Prüfungs-Überraschung: (1) Topographieschutz (Halbleiter) — wird häufig vergessen; (2) Sortenschutzrecht — ebenfalls ausdrücklich genannt. Daneben erfasst § 3 Abs. 2 PAO auch die Vertretung vor dem DPMA, dem Bundespatentgericht, dem EPA und anderen zuständigen Stellen des Auslands.",
    sourceIds: ["pao3"],
    sources: [
      { id: "pao3", label: "§ 3 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__3.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["berufsrecht", "patentanwaltsordnung", "taetigkeitsbereich", "sortenschutz", "topographie"],
    // FIX (verifiziert): § 3 Abs. 1 → § 3 Abs. 2 PAO (Enumeration steht in Abs. 2); Sortenschutz
    // und Topographien korrekt als in § 3 Abs. 2 PAO genannt belassen (autorisierte §-Tabelle
    // bestätigt: § 3 Abs. 2 ENTHÄLT Sortenschutz UND Topographien). Erklärung der Abs. 2-Logik
    // bereinigt. reviewStatus "rejected" → "reviewed".
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-003",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "PAO §§ 5–6 — Zulassung",
    title: "Zulassungsvoraussetzungen zum Patentanwalt (§§ 5–6 PAO)",
    prompt:
      "Welche Voraussetzungen müssen nach §§ 5–6 PAO kumulativ erfüllt sein, damit jemand als Patentanwalt zugelassen und in die Liste eingetragen wird?",
    options: [],
    answers: [],
    explanation:
      "§ 5 PAO (Zulassungsvoraussetzungen): Die Zulassung zur Patentanwaltschaft setzt insbesondere voraus: (1) Deutsche Staatsangehörigkeit oder Staatsangehörigkeit eines EU-/EWR-Mitgliedstaats bzw. gleichgestellter Personen; (2) Befähigung zum Patentanwalt — die technische Befähigung nach § 6 PAO (abgeschlossenes natur- oder ingenieurwissenschaftliches Hochschulstudium) sowie die bestandene Patentanwaltsprüfung (§§ 8 ff. PAO) nach geleisteter Ausbildungszeit; (3) Zuverlässigkeit und geordnete wirtschaftliche Verhältnisse. Fehlen diese, ist die Zulassung nach § 14 PAO zu versagen — § 14 PAO regelt die Versagungsgründe (etwa Unwürdigkeit infolge strafgerichtlicher Verurteilung oder Vermögensverfall). Nach Zulassung folgen Eintragung in die beim Vorstand der Patentanwaltskammer geführte Liste und Vereidigung.",
    sourceIds: ["pao5", "pao6", "pao14"],
    sources: [
      { id: "pao5", label: "§ 5 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__5.html" },
      { id: "pao6", label: "§ 6 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__6.html" },
      { id: "pao14", label: "§ 14 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__14.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["berufsrecht", "patentanwaltsordnung", "zulassung", "patentanwaltspruefung"],
    // FIX (Orchestrator, WebFetch verifiziert): § 6 PAO = "Technische Befähigung" (naturwiss./
    // techn. Studium), NICHT Versagungsgründe. Versagung der Zulassung = § 14 PAO. Erklärung
    // korrigiert (§ 6 = technische Befähigung; § 14 = Versagungsgründe), pao14 ergänzt.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-004",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "PAO § 39a — Verschwiegenheitspflicht",
    title: "Inhalt und Reichweite der Verschwiegenheitspflicht (§ 39a PAO)",
    prompt:
      "Was regelt § 39a PAO zur Verschwiegenheitspflicht des Patentanwalts — inhaltlich, personell und organisatorisch?",
    options: [],
    answers: [],
    explanation:
      "§ 39a PAO: (1) Inhalt: Der Patentanwalt ist zur Verschwiegenheit verpflichtet über alles, was ihm in Ausübung seines Berufs bekannt geworden ist. Das gilt auch nach Beendigung des Mandats. (2) Personell: Die Pflicht gilt für den Patentanwalt selbst. (3) Organisatorisch (§ 39a Abs. 2 PAO): Der Patentanwalt hat die bei seiner Berufsausübung beschäftigten Personen (Mitarbeiter, Hilfskräfte) zur Verschwiegenheit zu verpflichten. Diese sind ihrerseits zur Verschwiegenheit verpflichtet. (4) Ausnahmen: Gesetzlich zugelassene Offenbarungen (z.B. strafrechtliche Aussagepflichten). Das Zeugnisverweigerungsrecht des Patentanwalts (§§ 53 StPO, 383 ZPO) korrespondiert mit dieser Pflicht. Verletzung kann berufsrechtliche und strafrechtliche Folgen haben (§ 203 StGB).",
    sourceIds: ["pao39a"],
    sources: [
      { id: "pao39a", label: "§ 39a PAO", url: "https://www.gesetze-im-internet.de/patanwo/__39a.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["berufsrecht", "verschwiegenheit", "berufspflichten"],
    // REVIEW da-pao-004 (REVIEWED): § 39a PAO = Verschwiegenheitspflicht — korrekt. Inhalt,
    // personelle Reichweite, Pflicht zur Verpflichtung der Mitarbeiter (§ 39a Abs. 2 PAO) und
    // Korrespondenz zu § 53 StPO/§ 203 StGB sachlich richtig. Flashcard, Quelle vorhanden,
    // kein Leak.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-005",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "PAO § 53 ff. — Patentanwaltskammer",
    title: "Patentanwaltskammer: Rechtsform, Aufgaben, Organe",
    prompt:
      "Was ist die Patentanwaltskammer rechtlich, welche Kernaufgaben hat sie, und welche Organe sieht die PAO vor?",
    options: [],
    answers: [],
    explanation:
      "§ 53 PAO: Die Patentanwaltskammer ist eine Körperschaft des öffentlichen Rechts. Sie verwaltet sich selbst (Selbstverwaltung). Mitglieder sind alle zugelassenen Patentanwälte sowie Patentanwaltsgesellschaften. Aufgaben (§ 56 PAO): Wahrung der Berufspflichten, Förderung der Ausbildung, Beratung der Mitglieder, Erlass der Berufsordnung, Stellungnahmen zu Gesetzgebungsvorhaben. Organe (§§ 61 ff. PAO): (1) Vorstand — vertritt die Kammer nach außen, übt Berufsaufsicht, führt die Liste der Patentanwälte; (2) Präsident/Präsidentin — Vorsitz des Vorstands; (3) Patentanwaltskammer-Versammlung (§§ 78–82 PAO) — oberstes Beschlussorgan (alle Mitglieder); § 78 PAO regelt die Einberufung, § 81 PAO Wahlen/Beschlüsse, § 82 PAO die Aufgaben der Kammerversammlung (Satzung, Berufsordnung, Haushalt).",
    sourceIds: ["pao53", "pao56", "pao82"],
    sources: [
      { id: "pao53", label: "§ 53 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__53.html" },
      { id: "pao56", label: "§ 56 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__56.html" },
      { id: "pao82", label: "§ 82 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__82.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["berufsrecht", "patentanwaltskammer", "selbstverwaltung"],
    // FIX (verifiziert): § 73 PAO → §§ 78–82 PAO (Kammerversammlung: Einberufung § 78,
    // Wahlen/Beschlüsse § 81, Aufgaben § 82). Source pao82 ergänzt.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-006",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "PAO § 26 — Kanzleipflicht",
    title: "Kanzleipflicht und Niederlassungspflicht (§ 26 PAO)",
    prompt:
      "Was schreibt § 26 PAO zur Kanzleipflicht vor, und welche Ausnahmen sind möglich?",
    options: [],
    answers: [],
    explanation:
      "§ 26 Abs. 1 PAO: Der Patentanwalt muss im Inland eine Kanzlei einrichten und unterhalten, in der er für Mandanten und Behörden erreichbar ist (Niederlassungspflicht). Die Kanzlei muss tatsächlich besetzt/erreichbar sein — eine bloße Briefkastenadresse genügt nicht. § 26 Abs. 2 PAO: Daneben kann der Patentanwalt weitere Kanzleien (Zweigstellen) einrichten; für jede Zweigstelle gelten dieselben Anforderungen. Ausnahmen/Erleichterungen: Bei Sozietäten, Partnerschaftsgesellschaften oder zugelassenen Berufsausübungsgesellschaften (§§ 52b ff. PAO, Reform 2022; Patentanwaltsgesellschaft: § 52o PAO) kann die Kanzleipflicht gemeinsam erfüllt werden. Verletzung der Kanzleipflicht ist ein Berufsrechtsverstoß, der zur Rüge oder berufsgerichtlichem Verfahren führen kann.",
    sourceIds: ["pao26", "pao52b"],
    sources: [
      { id: "pao26", label: "§ 26 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__26.html" },
      { id: "pao52b", label: "§ 52b PAO", url: "https://www.gesetze-im-internet.de/patanwo/__52b.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["berufsrecht", "kanzleipflicht", "niederlassung"],
    // FIX (verifiziert): § 52b PAO-Verweis auf geltende Rechtslage 2022 aktualisiert → §§ 52b ff.
    // PAO / Patentanwaltsgesellschaft § 52o PAO; kein zwingendes Mehrheitserfordernis mehr. Source
    // pao52b ergänzt.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-007",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "PAO §§ 43 ff. — Berufsaufsicht",
    title: "Berufsaufsicht: Rüge und berufsgerichtliches Verfahren",
    prompt:
      "Welche Aufsichtsmaßnahmen kennt die PAO bei Berufspflichtverstößen, und wer ist zuständig?",
    options: [],
    answers: [],
    explanation:
      "Die Berufsaufsicht obliegt dem Vorstand der Patentanwaltskammer. Bei Berufspflichtverstößen kommt in Betracht: (1) Rüge (§ 70 PAO) — schriftliche Missbilligung durch den Vorstand der Kammer bei leichteren Verstößen; der Patentanwalt kann Einspruch einlegen, über den die Kammer-Versammlung entscheidet. (2) Berufsgerichtliches Verfahren (§§ 88 ff. PAO) — bei schwereren Verstößen; das Berufsgericht kann folgende Maßnahmen nach § 96 PAO verhängen: Warnung, Verweis, Geldbuße bis 25.000 €, Verbot, auf bestimmten Gebieten tätig zu sein, Ausschließung aus der Patentanwaltschaft.",
    sourceIds: ["pao70", "pao96"],
    sources: [
      { id: "pao70", label: "§ 70 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__70.html" },
      { id: "pao96", label: "§ 96 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__96.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["berufsrecht", "berufsaufsicht", "ruege", "berufsgerichtlich"],
    // FIX (verifiziert): Rüge § 44 → § 70 PAO (Rügerecht des Vorstands der Patentanwaltskammer);
    // "anwaltsgerichtlich" → "berufsgerichtlich"; sources pao43/pao44 → pao70/pao96.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-008",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "PAO §§ 40–42 — Sozietät und Haftpflicht",
    title: "Berufsausübungsgemeinschaft und Berufshaftpflichtversicherung",
    prompt:
      "In welchen Organisationsformen darf der Patentanwalt seinen Beruf ausüben, und welche Versicherungspflicht besteht?",
    options: [],
    answers: [],
    explanation:
      "Berufsausübungsgesellschaften (Reform 2022): §§ 52b ff. PAO erlauben Sozietäten, Partnerschaftsgesellschaften und Patentanwaltsgesellschaften (GmbH oder AG, § 52o PAO). Nach der Berufsrechtsreform 2022 besteht KEIN zwingendes Kapital- oder Stimmrechtsmehrheitserfordernis mehr für Patentanwälte; interprofessionelle Gesellschaften mit Nicht-PA-Mehrheit sind unter den gesetzlichen Voraussetzungen zulässig. Die Firma muss den Beruf und die Rechtsform erkennen lassen. Berufshaftpflichtversicherung (§ 45 PAO): Jeder Patentanwalt ist verpflichtet, eine Berufshaftpflichtversicherung zur Deckung der sich aus seiner Berufstätigkeit ergebenden Haftpflichtgefahren zu unterhalten. Mindestdeckungssumme und Anforderungen werden durch die Berufsordnung konkretisiert.",
    sourceIds: ["pao45", "pao52b", "pao52o"],
    sources: [
      { id: "pao45", label: "§ 45 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__45.html" },
      { id: "pao52b", label: "§ 52b PAO", url: "https://www.gesetze-im-internet.de/patanwo/__52b.html" },
      { id: "pao52o", label: "§ 52o PAO", url: "https://www.gesetze-im-internet.de/patanwo/__52o.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["berufsrecht", "sozietaet", "haftpflichtversicherung", "patentanwaltsgesellschaft"],
    // FIX (verifiziert): § 52c a.F. / Mehrheitserfordernis pre-2022 → geltende Rechtslage 2022:
    // §§ 52b ff. PAO, Patentanwaltsgesellschaft § 52o PAO, kein zwingendes Mehrheitserfordernis.
    // Source pao52o ergänzt.
    reviewStatus: "reviewed",
  },

  // ─── MC SINGLE / MULTI da-pao-009 … da-pao-030 ──────────────────────────

  {
    id: "da-pao-009",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO § 3 — Tätigkeitsbereich",
    title: "§ 3 PAO: Vollständiger Tätigkeitsbereich",
    prompt:
      "Welches der folgenden Schutzrechtsgebiete ist im Tätigkeitsbereich des Patentanwalts nach § 3 Abs. 2 PAO NICHT ausdrücklich aufgeführt?",
    options: [
      "Topographieschutz (Halbleiter)",
      "Sortenschutzrecht",
      "Urheberrecht",
      "Ergänzende Schutzzertifikate",
    ],
    answers: [2],
    explanation:
      "§ 3 Abs. 2 PAO zählt zum Tätigkeitsbereich des Patentanwalts: Patente, ergänzende Schutzzertifikate, Gebrauchsmuster, eingetragene Designs, Topographien von Halbleitererzeugnissen, Marken und Sortenschutzrechte. Das Urheberrecht ist dort nicht aufgeführt — es fällt grundsätzlich in den Tätigkeitsbereich des Rechtsanwalts. Ein Patentanwalt darf urheberrechtliche Fragen nur insoweit beraten, als sie in unmittelbarem Zusammenhang mit gewerblichem Rechtsschutz stehen und das RDG dies zulässt.",
    sourceIds: ["pao3"],
    sources: [
      { id: "pao3", label: "§ 3 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__3.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["berufsrecht", "taetigkeitsbereich", "sortenschutz", "topographie"],
    // FIX (verifiziert): § 3 Abs. 1 → § 3 Abs. 2 PAO in Prompt und Explanation (Enumeration steht
    // in Abs. 2). Sortenschutz und Topographien korrekt als in § 3 Abs. 2 PAO genannt belassen
    // (autorisierte §-Tabelle bestätigt). Lösung Index 2 (Urheberrecht nicht aufgeführt) korrekt.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-010",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO § 1 / § 39 — Beruf und Berufspflichten",
    title: "Gewissenhafte Berufsausübung (§ 39 PAO)",
    prompt:
      "Was fordert § 39 PAO von der Berufsausübung des Patentanwalts?",
    options: [
      "Der Patentanwalt ist ausschließlich den Interessen seines Mandanten verpflichtet und darf jede zulässige Strategie einsetzen.",
      "Der Patentanwalt hat seinen Beruf gewissenhaft auszuüben und sich innerhalb und außerhalb des Berufs der Achtung und des Vertrauens würdig zu erweisen, die sein Beruf erfordert.",
      "Eine gewissenhafte Berufsausübung verlangt nur die formale Einhaltung gesetzlicher Fristen.",
      "§ 39 PAO verpflichtet den Patentanwalt ausschließlich gegenüber Gerichten, nicht gegenüber Mandanten.",
    ],
    answers: [1],
    explanation:
      "§ 39 Abs. 1 PAO: Der Patentanwalt hat seinen Beruf gewissenhaft auszuüben. § 39 Abs. 2 PAO: Er hat sich innerhalb und außerhalb des Berufs der Achtung und des Vertrauens würdig zu erweisen, die sein Beruf erfordert. Diese Generalklausel ist Grundlage vieler Berufspflichten und wird durch die Berufsordnung der Patentanwaltskammer konkretisiert. Die Mandanteninteressen sind zwar maßgeblich, aber nicht schrankenlos — die Pflichten als Organ der Rechtspflege (§ 1 PAO) und gegenüber der Allgemeinheit bilden eine immanente Grenze.",
    sourceIds: ["pao39"],
    sources: [
      { id: "pao39", label: "§ 39 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__39.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["berufsrecht", "berufspflichten", "gewissenhaftigkeit"],
    // REVIEW da-pao-010 (REVIEWED): § 39 Abs. 1/2 PAO (gewissenhafte Berufsausübung + Achtung/
    // Vertrauen innerhalb/außerhalb des Berufs) korrekt zitiert. Lösung Index 1 eindeutig richtig,
    // Distraktoren plausibel falsch, 4 Optionen, Quelle, kein Leak.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-011",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO § 39a — Verschwiegenheitspflicht (Fall)",
    title: "Fall: Verschwiegenheitspflicht — Mandatswechsel",
    prompt:
      "Patentanwalt P hat Mandant A jahrelang bei der Anmeldung und Verteidigung eines Patents beraten. Das Mandat ist beendet. Nun ersucht Konkurrent B den P um Beratung in einem Einspruchsverfahren gegen dasselbe Patent von A. Welche Berufspflicht ist vorrangig zu prüfen?",
    options: [
      "Die Fortbildungspflicht nach § 39 Abs. 3 PAO — P muss über aktuelles Wissen verfügen.",
      "Die Verschwiegenheitspflicht nach § 39a PAO — P darf Informationen aus dem früheren Mandat nicht verwenden; zudem liegt ein Interessenkonflikt (widerstreitende Interessen) vor.",
      "Die Kanzleipflicht nach § 26 PAO — P muss prüfen, ob B am Kanzleiort erreichbar ist.",
      "Es besteht keine berufsrechtliche Pflicht, da das Mandat mit A beendet ist.",
    ],
    answers: [1],
    explanation:
      "§ 39a PAO: Die Verschwiegenheitspflicht gilt auch nach Beendigung des Mandats — P darf Informationen aus dem Mandat mit A nicht für B verwenden. Zusätzlich greift das Verbot widerstreitender Interessen (§ 39 Abs. 1 PAO i.V.m. § 3 Abs. 2 BORA analog bzw. berufsordnungsrechtliche Generalklausel): P darf keine Tätigkeit übernehmen, bei der er gegen einen früheren Mandanten vorgeht, wenn er dabei Kenntnisse aus dem früheren Mandat einsetzen würde oder einsetzen könnte. Das Mandat mit B wäre abzulehnen.",
    sourceIds: ["pao39a", "pao39"],
    sources: [
      { id: "pao39a", label: "§ 39a PAO", url: "https://www.gesetze-im-internet.de/patanwo/__39a.html" },
      { id: "pao39", label: "§ 39 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__39.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["berufsrecht", "verschwiegenheit", "interessenkonflikt"],
    // REVIEW da-pao-011 (REVIEWED): § 39a PAO (Verschwiegenheit gilt auch nach Mandatsende) +
    // Verbot widerstreitender Interessen korrekt als vorrangige Pflicht erkannt; Lösung Index 1
    // eindeutig, Distraktoren klar falsch, 4 Optionen, kein Leak. Hinweis Autor: "§ 3 Abs. 2 BORA
    // analog" ist eine lockere Anlehnung (BORA = RA-Berufsordnung; PAO hat eigene Berufsordnung);
    // durch "bzw. berufsordnungsrechtliche Generalklausel" hinreichend abgesichert — kein Fehler.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-012",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO § 5 — Zulassung",
    title: "Technisch-naturwissenschaftliche Befähigung als Zulassungsvoraussetzung",
    prompt:
      "Welche Anforderung an die technisch-naturwissenschaftliche Befähigung stellt § 5 PAO als Zulassungsvoraussetzung?",
    options: [
      "Ein abgeschlossenes Jurastudium mit Schwerpunkt Technikrecht reicht aus.",
      "Ein abgeschlossenes Studium der Rechtswissenschaften an einer deutschen Universität ist erforderlich.",
      "Ein abgeschlossenes Studium der Naturwissenschaften oder der Ingenieurwissenschaften an einer deutschen oder gleichwertigen ausländischen Hochschule ist erforderlich.",
      "Eine abgeschlossene Berufsausbildung in einem technischen Beruf genügt.",
    ],
    answers: [2],
    explanation:
      "§ 5 Abs. 2 Nr. 2 PAO (sinngemäß): Die Befähigung zum Patentanwalt setzt u.a. ein abgeschlossenes naturwissenschaftliches oder ingenieurwissenschaftliches Hochschulstudium voraus. Ein reines Jurastudium oder eine Berufsausbildung erfüllen diese Voraussetzung nicht — der Patentanwalt soll technische Schutzrechte auf fundierter Sachkenntnis hin beurteilen können. Erst nach dieser technischen Ausbildung und einer Ausbildungszeit (§ 7 PAO: grundsätzlich 34 Monate, davon mindestens 26 Monate bei einem Patentanwalt oder Patentassessor, 2 Monate beim DPMA und 6 Monate beim Bundespatentgericht) folgt die Patentanwaltsprüfung.",
    sourceIds: ["pao5", "pao7"],
    sources: [
      { id: "pao5", label: "§ 5 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__5.html" },
      { id: "pao7", label: "§ 7 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__7.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["berufsrecht", "zulassung", "technische-befaehigung"],
    // FIX (verifiziert): "26 Mon + 8 Mon DPMA" → "26 Mon Kanzlei/Patentassessor + 2 Mon DPMA
    // + 6 Mon BPatG" (§ 7 PAO). Source pao7 ergänzt.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-013",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO §§ 53 ff. — Patentanwaltskammer",
    title: "Rechtsnatur der Patentanwaltskammer",
    prompt:
      "Welche Aussage zur Rechtsnatur der Patentanwaltskammer nach § 53 PAO ist zutreffend?",
    options: [
      "Die Patentanwaltskammer ist eine eingetragene Vereinigung des Privatrechts (e.V.).",
      "Die Patentanwaltskammer ist eine Behörde des Bundes und untersteht dem Bundesjustizministerium.",
      "Die Patentanwaltskammer ist eine Körperschaft des öffentlichen Rechts, die sich selbst verwaltet.",
      "Die Patentanwaltskammer ist eine GmbH, an der alle Patentanwälte Pflichtmitglieder sind.",
    ],
    answers: [2],
    explanation:
      "§ 53 Abs. 1 PAO: Die Patentanwaltskammer ist eine Körperschaft des öffentlichen Rechts. Sie hat das Recht zur Selbstverwaltung (Selbstverwaltungskörperschaft). Mitglieder sind kraft Gesetzes alle zugelassenen Patentanwälte und Patentanwaltsgesellschaften — die Mitgliedschaft ist Pflicht, kein freiwilliger Beitritt. Die Kammer ist keine staatliche Behörde, aber sie nimmt öffentliche Aufgaben wahr (Berufsaufsicht, Erlass von Berufsordnung, Führung der Patentanwaltsliste).",
    sourceIds: ["pao53"],
    sources: [
      { id: "pao53", label: "§ 53 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__53.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["berufsrecht", "patentanwaltskammer", "koerperschaft-oeffentlichen-rechts"],
    // REVIEW da-pao-013 (REVIEWED): § 53 PAO = Körperschaft des öffentlichen Rechts mit
    // Selbstverwaltung, Pflichtmitgliedschaft — korrekt. Lösung Index 2 eindeutig, Distraktoren
    // (e.V./Behörde/GmbH) klar falsch, 4 Optionen, kein Leak.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-014",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO § 26 — Kanzleipflicht (Fall)",
    title: "Fall: Kanzleipflicht — ausschließlicher Homeoffice-Betrieb",
    prompt:
      "Patentanwalt P möchte seine Kanzlei ausschließlich aus seinem privaten Wohnhaus heraus führen, ohne ein separates Büro. Er ist telefonisch und per E-Mail erreichbar. Genügt dies der Kanzleipflicht des § 26 PAO?",
    options: [
      "Ja — moderne Kommunikationsmittel erfüllen die Anforderungen der Kanzleipflicht vollständig.",
      "Nein — § 26 PAO verlangt immer ein von der Privatwohnung getrenntes Büro in einem Bürogebäude.",
      "Es kommt darauf an, ob die Kanzlei tatsächlich eingerichtet ist und Mandanten und Behörden dort erreichbar sind; ein Homeoffice kann ausreichen, wenn die Kanzlei als solche erkennbar und tatsächlich funktionsfähig betrieben wird.",
      "Ja — der Kanzleisitz muss nur beim DPMA angemeldet sein, unabhängig vom tatsächlichen Betrieb.",
    ],
    answers: [2],
    explanation:
      "§ 26 Abs. 1 PAO: Der Patentanwalt muss im Inland eine Kanzlei einrichten und unterhalten. Die Norm verlangt eine tatsächlich funktionsfähige Kanzlei — d.h. eine Einrichtung, unter der er für Mandanten und Behörden erreichbar und tätig ist. Ein reiner Briefkasten genügt nicht. Ein Homeoffice kann die Kanzleipflicht erfüllen, wenn es als Kanzlei eingerichtet (erkennbar, funktionsfähig, tatsächlich genutzt) ist und kein reiner Scheinbetrieb vorliegt. Option B ist zu eng (keine gesetzliche Pflicht zu einem Bürogebäude), Option A ist zu weit (bloße E-Mail-Erreichbarkeit ohne eingerichtete Kanzlei genügt nicht).",
    sourceIds: ["pao26"],
    sources: [
      { id: "pao26", label: "§ 26 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__26.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["berufsrecht", "kanzleipflicht", "niederlassung"],
    // REVIEW da-pao-014 (REVIEWED): § 26 PAO Homeoffice-Fall — die differenzierte Lösung (Index 2:
    // kommt auf tatsächliche Funktionsfähigkeit/Erkennbarkeit an, Homeoffice kann genügen) ist
    // rechtlich korrekt; Option A zu weit, B zu eng, D falsch. 4 Optionen, kein Leak.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-015",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO § 70 — Rüge",
    title: "Rüge: Voraussetzungen und Rechtsmittel",
    prompt:
      "Welche Aussage zur Rüge nach § 70 PAO ist zutreffend?",
    options: [
      "Die Rüge kann nur vom Berufsgericht ausgesprochen werden, nicht vom Vorstand der Kammer.",
      "Die Rüge ist eine schriftliche Missbilligung durch den Vorstand der Patentanwaltskammer; der Patentanwalt kann dagegen Einspruch einlegen.",
      "Eine Rüge hat dieselbe Wirkung wie ein Verweis durch das Berufsgericht.",
      "Die Rüge ist im Bundeszentralregister einzutragen.",
    ],
    answers: [1],
    explanation:
      "§ 70 PAO: Die Rüge ist eine schriftliche Missbilligung, die der Vorstand der Patentanwaltskammer gegenüber einem Mitglied aussprechen kann, wenn dieses schuldhaft gegen Berufspflichten verstoßen hat und die Schuld als gering anzusehen ist. Gegen die Rüge kann der Betroffene Einspruch einlegen; über den Einspruch entscheidet die Patentanwaltskammer-Versammlung oder ein dafür vorgesehenes Gremium. Die Rüge ist ein milderes Aufsichtsmittel als das berufsgerichtliche Verfahren. Sie wird nicht im Bundeszentralregister eingetragen.",
    sourceIds: ["pao70"],
    sources: [
      { id: "pao70", label: "§ 70 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__70.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["berufsrecht", "berufsaufsicht", "ruege"],
    // FIX (verifiziert): Rüge § 44 → § 70 PAO in topic, prompt, explanation und source; pao44
    // → pao70. "anwaltsgerichtlich" → "berufsgerichtlich".
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-016",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "multi",
    topic: "PAO § 3 — Vertretungsbefugnisse",
    title: "Vor welchen Stellen vertritt der Patentanwalt kraft § 3 PAO?",
    prompt:
      "Welche der folgenden Stellen darf der Patentanwalt nach § 3 PAO in Angelegenheiten des gewerblichen Rechtsschutzes vertreten? (Mehrere Antworten möglich)",
    options: [
      "Deutsches Patent- und Markenamt (DPMA)",
      "Europäisches Patentamt (EPA)",
      "Bundespatentgericht (BPatG)",
      "Landgericht in einer Patentverletzungsklage als alleiniger Prozessvertreter ohne Rechtsanwalt",
    ],
    answers: [0, 1, 2],
    explanation:
      "§ 3 Abs. 1 PAO: Der Patentanwalt ist zur Vertretung vor dem DPMA, dem EPA und dem BPatG (in Nichtigkeits-, Einspruchs- und Beschwerdeverfahren) zugelassen. Vor dem Bundesgerichtshof in Patentsachen kann er gemeinsam mit einem Rechtsanwalt auftreten. Vor den ordentlichen Gerichten (Landgericht) in Patentverletzungsklagen besteht kein alleiniges Vertretungsrecht — dort gilt Anwaltszwang für Rechtsanwälte; der Patentanwalt kann als technischer Berater beigeordnet werden (§ 143 Abs. 3 PatG). Option D ist daher falsch.",
    sourceIds: ["pao3"],
    sources: [
      { id: "pao3", label: "§ 3 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__3.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["berufsrecht", "vertretungsbefugnis", "dpma", "epa", "bpatg"],
    // REVIEW da-pao-016 (REVIEWED): § 3 PAO Vertretungsbefugnis — DPMA, EPA, BPatG zutreffend
    // (answers [0,1,2]); LG-Patentverletzungsklage als alleiniger Prozessvertreter ohne RA
    // zutreffend AUSGESCHLOSSEN (Anwaltszwang; Patentanwalt nur als technischer Beistand, § 143
    // Abs. 3 PatG). Multi-Logik korrekt, kein Leak. Hinweis: Vertretungsbefugnis folgt aus
    // § 4 PAO/§ 3 PAO-Systematik — Sammelzitat "§ 3 PAO" hier tragfähig.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-017",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO § 39a — Verschwiegenheit — Mitarbeiter (Fall)",
    title: "Fall: Verpflichtung von Kanzleimitarbeitern zur Verschwiegenheit",
    prompt:
      "Patentanwältin P stellt eine neue Sekretärin S ein, die Zugang zu Mandantenakten erhält. Welche Pflicht trifft P nach § 39a Abs. 2 PAO gegenüber S?",
    options: [
      "P muss S nur dann zur Verschwiegenheit verpflichten, wenn S direkten Mandantenkontakt hat.",
      "Eine Verpflichtung der Mitarbeiter zur Verschwiegenheit ist freiwillig und vom Ermessen der Kanzlei abhängig.",
      "P ist verpflichtet, S zur Verschwiegenheit zu verpflichten; S ist ihrerseits kraft dieser Verpflichtung zur Verschwiegenheit gehalten.",
      "Die Verschwiegenheitspflicht der Mitarbeiter folgt automatisch aus dem Arbeitsvertrag ohne gesonderte Verpflichtung.",
    ],
    answers: [2],
    explanation:
      "§ 39a Abs. 2 PAO: Der Patentanwalt ist verpflichtet, die bei seiner Berufsausübung beschäftigten Personen zur Verschwiegenheit zu verpflichten. Diese Pflicht trifft P unabhängig davon, ob die Mitarbeiter direkten Mandantenkontakt haben — maßgeblich ist der Zugang zu mandantenbezogenen Informationen. Die Verpflichtung muss tatsächlich vorgenommen werden (nicht nur im Arbeitsvertrag implizit enthalten); häufig geschieht dies durch eine ausdrückliche schriftliche Erklärung. S ist nach dieser Verpflichtung ihrerseits zur Verschwiegenheit verpflichtet.",
    sourceIds: ["pao39a"],
    sources: [
      { id: "pao39a", label: "§ 39a PAO", url: "https://www.gesetze-im-internet.de/patanwo/__39a.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["berufsrecht", "verschwiegenheit", "mitarbeiter"],
    // REVIEW da-pao-017 (REVIEWED): § 39a Abs. 2 PAO — Pflicht zur Verpflichtung beschäftigter
    // Personen zur Verschwiegenheit (unabhängig von direktem Mandantenkontakt; tatsächliche
    // Verpflichtung nötig). Lösung Index 2 korrekt, Distraktoren klar falsch, 4 Optionen, kein
    // Leak.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-018",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO § 45 — Berufshaftpflichtversicherung",
    title: "Berufshaftpflichtversicherung — Pflicht und Konsequenz",
    prompt:
      "Welche Konsequenz hat das Fehlen einer vorgeschriebenen Berufshaftpflichtversicherung nach § 45 PAO für den Patentanwalt?",
    options: [
      "Keine — die Versicherungspflicht ist eine Obliegenheit ohne Sanktion.",
      "Der Patentanwalt verliert das Recht zur Führung der Berufsbezeichnung, kann aber weiter tätig sein.",
      "Die Nichterfüllung der Versicherungspflicht kann als Berufspflichtverletzung Gegenstand eines berufsgerichtlichen Verfahrens sein und im Extremfall zur Ausschließung führen.",
      "Das Mandat mit einem Mandanten erlischt automatisch, wenn die Versicherung erlischt.",
    ],
    answers: [2],
    explanation:
      "§ 45 PAO verpflichtet den Patentanwalt zum Abschluss und zur Aufrechterhaltung einer Berufshaftpflichtversicherung. Die Verletzung dieser Pflicht ist ein Berufspflichtsverstoß im Sinne der PAO und kann Gegenstand eines berufsgerichtlichen Verfahrens werden (berufsgerichtliche Maßnahmen: § 96 PAO). Im Extremfall — etwa bei hartnäckiger Weigerung — droht die Ausschließung aus der Patentanwaltschaft. Das Mandat erlischt nicht automatisch, aber der Mandant kann ggf. keinen adäquaten Haftpflichtschutz in Anspruch nehmen.",
    sourceIds: ["pao45"],
    sources: [
      { id: "pao45", label: "§ 45 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__45.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Rechtsfolge",
    skillTags: ["berufsrecht", "haftpflichtversicherung", "berufspflichten"],
    // FIX (Orchestrator, WebFetch verifiziert): "anwaltsgerichtlich" → "berufsgerichtlich"
    // (Patentanwälte: Berufsgericht, § 96 PAO Maßnahmen). § 88 PAO = "Rechtsstellung der
    // patentanwaltlichen Mitglieder" (nicht das Verfahren) → Verweis auf § 96 PAO korrigiert.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-019",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO §§ 78–82 — Kammerorgane",
    title: "Oberstes Beschlussorgan der Patentanwaltskammer",
    prompt:
      "Welches Organ der Patentanwaltskammer ist nach §§ 78–82 PAO das oberste Beschlussorgan?",
    options: [
      "Der Vorstand der Patentanwaltskammer",
      "Die Patentanwaltskammer-Versammlung",
      "Das Berufsgericht",
      "Der Präsident der Patentanwaltskammer",
    ],
    answers: [1],
    explanation:
      "§§ 78–82 PAO: Die Patentanwaltskammer-Versammlung ist das oberste Beschlussorgan der Kammer. § 78 PAO regelt die Einberufung der Versammlung, § 81 PAO Wahlen und Beschlüsse, § 82 PAO die Aufgaben der Kammerversammlung. Alle Mitglieder der Kammer sind zur Teilnahme berechtigt. Sie beschließt u.a. über die Satzung, die Berufsordnung, den Haushaltsplan, wählt den Vorstand und entscheidet über Einsprüche gegen Rügen. Der Vorstand (§§ 61 ff. PAO) führt die laufenden Geschäfte und übt die Berufsaufsicht aus, ist aber dem Beschlussorgan Kammer-Versammlung untergeordnet.",
    sourceIds: ["pao53", "pao82"],
    sources: [
      { id: "pao53", label: "§ 53 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__53.html" },
      { id: "pao82", label: "§ 82 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__82.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["berufsrecht", "patentanwaltskammer", "kammer-versammlung"],
    // FIX (verifiziert): § 73 PAO → §§ 78–82 PAO (Einberufung § 78, Wahlen/Beschlüsse § 81,
    // Aufgaben § 82) in topic, prompt und explanation. Source pao73 → pao82.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-020",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO § 52o — Patentanwaltsgesellschaft (Fall)",
    title: "Fall: Patentanwaltsgesellschaft — Zulässigkeit nach Reform 2022",
    prompt:
      "Drei Personen möchten eine Patentanwaltsgesellschaft mbH gründen: Patentanwalt A (Geschäftsführer), Wirtschaftsprüfer W und Steuerberaterin S. W und S halten je 40 % der Anteile, A hält 20 %. Ist die Gesellschaft nach der geltenden Rechtslage (§§ 52b ff., § 52o PAO, Reform 2022) grundsätzlich zulässig?",
    options: [
      "Nein — Patentanwälte müssen stets die Mehrheit der Gesellschaftsanteile halten; A mit 20 % unterschreitet diese Schwelle.",
      "Ja — nach der Berufsrechtsreform 2022 besteht kein zwingendes Kapital- oder Stimmrechtsmehrheitserfordernis mehr; entscheidend sind die sonstigen Voraussetzungen des § 52o PAO (z.B. Firma, Berufsbezeichnung, Berufshaftpflicht).",
      "Nein — interprofessionelle Gesellschaften mit Nicht-Patentanwälten als Mehrheitsgesellschafter sind generell verboten.",
      "Ja — aber nur, wenn W und S auf ihre Stimmrechte verzichten.",
    ],
    answers: [1],
    explanation:
      "§§ 52b ff. PAO, § 52o PAO (Berufsrechtsreform 2022): Die frühere strikte Anforderung, dass Patentanwälte die Mehrheit der Gesellschaftsanteile und Stimmrechte halten müssen (§ 52c PAO a.F.), wurde durch die Reform 2022 abgeschafft. Nach geltendem Recht ist kein zwingendes Kapital- oder Stimmrechtsmehrheitserfordernis mehr vorgeschrieben. Die Patentanwaltsgesellschaft (§ 52o PAO) muss die sonstigen Voraussetzungen erfüllen: u.a. gesellschaftsrechtliche Anforderungen, Führung der Berufsbezeichnung in der Firma, Berufshaftpflichtversicherung. Im vorliegenden Fall mit A als Geschäftsführer und 20 % Anteil ist die Struktur daher grundsätzlich zulässig — die Mehrheitsgesellschafter W und S sind kein Hindernis mehr.",
    sourceIds: ["pao52o", "pao52b"],
    sources: [
      { id: "pao52o", label: "§ 52o PAO", url: "https://www.gesetze-im-internet.de/patanwo/__52o.html" },
      { id: "pao52b", label: "§ 52b PAO", url: "https://www.gesetze-im-internet.de/patanwo/__52b.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["berufsrecht", "patentanwaltsgesellschaft", "sozietaet"],
    // FIX (verifiziert): Fall vollständig auf geltende Rechtslage 2022 umgestellt: § 52c a.F.
    // aufgehoben; kein zwingendes Mehrheitserfordernis mehr; § 52o PAO = Patentanwaltsgesellschaft.
    // Prompt, Optionen, Keyed-Answer (neu Index 1), Explanation und sources komplett neu gefasst.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-021",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO — Vergütung / RVG-Bezug",
    title: "Vergütung des Patentanwalts — Verhältnis zum RVG",
    prompt:
      "Welche Aussage zur Vergütung des Patentanwalts ist zutreffend?",
    options: [
      "Der Patentanwalt ist stets an die Sätze des RVG gebunden; Individualvereinbarungen sind verboten.",
      "Die Vergütung des Patentanwalts kann frei vereinbart werden; fehlt eine Vereinbarung, gilt das RVG (oder die PAO-Gebührenordnung) als Auffangregelung.",
      "Der Patentanwalt darf keine höheren Gebühren als im RVG vorgesehen vereinbaren.",
      "Die Vergütungspflicht des Mandanten entsteht erst mit rechtskräftigem Abschluss des Verfahrens.",
    ],
    answers: [1],
    explanation:
      "Der Patentanwalt kann seine Vergütung grundsätzlich mit dem Mandanten frei vereinbaren (Vergütungsvereinbarung). Fehlt eine ausdrückliche Vereinbarung, richtet sich die Vergütung nach dem Rechtsanwaltsvergütungsgesetz (RVG), das auf Patentanwälte entsprechend angewendet wird (§ 1 Abs. 3 RVG). Eine Vereinbarung, die unter den gesetzlichen Mindestgebühren bleibt, kann berufsrechtlich problematisch sein (Dumpinggebühren). Erfolgshonorare waren lange verboten und sind nur unter engen Voraussetzungen zulässig. Eine Deckelung auf RVG-Sätze nach oben existiert nicht — höhere Sätze können vereinbart werden.",
    sourceIds: ["rvg"],
    sources: [
      { id: "rvg", label: "RVG", url: "https://www.gesetze-im-internet.de/rvg/" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["berufsrecht", "verguetung", "rvg"],
    // FIX (verifiziert): Source pao39 (§ 39 PAO trägt keine Vergütungsregel) → rvg (RVG als
    // Auffangregelung für Patentanwaltsvergütung, § 1 Abs. 3 RVG).
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-022",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO — Verhältnis zu Rechtsanwälten / RDG",
    title: "Fall: Rechtsberatungsbefugnis des Patentanwalts (RDG-Bezug)",
    prompt:
      "Mandant M fragt Patentanwalt P, ob P ihn auch in einem allgemeinen Vertragsstreit (ohne Bezug zum gewerblichen Rechtsschutz) beraten darf. Wie ist die Rechtslage?",
    options: [
      "Ja — als Organ der Rechtspflege ist der Patentanwalt zur Rechtsberatung in allen Rechtsfragen befugt.",
      "Nein — Rechtsberatung außerhalb des gewerblichen Rechtsschutzes ist dem Patentanwalt nach § 3 PAO i.V.m. RDG grundsätzlich nicht gestattet; für allgemeine Vertragsstreitigkeiten ohne IP-Bezug ist ein Rechtsanwalt zuständig.",
      "Ja — der Patentanwalt darf immer dann beraten, wenn er dies für sinnvoll hält.",
      "Nein — aber der Patentanwalt kann dem Mandanten einen Rechtsanwalt empfehlen, was bereits eine unzulässige Rechtsdienstleistung darstellt.",
    ],
    answers: [1],
    explanation:
      "§ 3 Abs. 2 PAO berechtigt den Patentanwalt zur Rechtsberatung und -vertretung in Angelegenheiten des gewerblichen Rechtsschutzes. Das Rechtsdienstleistungsgesetz (RDG, § 3) erlaubt Rechtsdienstleistungen nur, soweit sie durch Gesetz erlaubt sind. Allgemeine Vertragsstreitigkeiten ohne Bezug zum gewerblichen Rechtsschutz fallen nicht in den Tätigkeitsbereich des § 3 PAO — für diese Beratung ist der Rechtsanwalt zuständig. Eine bloße Empfehlung eines Rechtsanwalts ist selbstverständlich keine unerlaubte Rechtsdienstleistung (Option D ist falsch).",
    sourceIds: ["pao3"],
    sources: [
      { id: "pao3", label: "§ 3 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__3.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["berufsrecht", "rechtsberatung", "rdg", "taetigkeitsbereich"],
    // REVIEW da-pao-022 (REVIEWED): Rechtslage korrekt — Rechtsberatung außerhalb des gewerbl.
    // Rechtsschutzes ist dem Patentanwalt nicht gestattet (§ 3/§ 4 PAO i.V.m. RDG); für allg.
    // Vertragsstreit ohne IP-Bezug ist der RA zuständig; bloße RA-Empfehlung ist KEINE unerlaubte
    // Rechtsdienstleistung (Option D zu Recht falsch). Lösung Index 1 eindeutig, 4 Optionen, kein
    // Leak.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-023",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO § 96 — Berufsgerichtliche Maßnahmen",
    title: "Berufsgerichtliche Maßnahmen — Eskalationsstufen",
    prompt:
      "Welche der folgenden Maßnahmen kann das Berufsgericht nach § 96 PAO bei Berufspflichtverstößen NICHT verhängen?",
    options: [
      "Warnung",
      "Verweis",
      "Geldbuße bis zu 25.000 Euro",
      "Freiheitsstrafe bis zu einem Jahr",
    ],
    answers: [3],
    explanation:
      "§ 96 PAO: Das Berufsgericht für Patentanwälte kann folgende berufsgerichtliche Maßnahmen verhängen: (1) Warnung, (2) Verweis, (3) Geldbuße bis zu 25.000 Euro, (4) Verbot, auf bestimmten Rechtsgebieten als Patentanwalt tätig zu sein (bis zu fünf Jahre), (5) Ausschließung aus der Patentanwaltschaft. Eine Freiheitsstrafe ist keine berufsgerichtliche Maßnahme — Freiheitsstrafen werden ausschließlich im Strafverfahren durch Strafgerichte verhängt. Das berufsgerichtliche Verfahren ist ein Berufsrechts-, kein Strafverfahren.",
    sourceIds: ["pao96"],
    sources: [
      { id: "pao96", label: "§ 96 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__96.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["berufsrecht", "berufsgerichtlich", "massnahmen"],
    // FIX (Orchestrator, WebFetch verifiziert): "Anwaltsgericht/anwaltsgerichtlich" → "Berufsgericht/
    // berufsgerichtlich" (Patentanwälte haben ein Berufsgericht; § 96 PAO = "Berufsgerichtliche
    // Maßnahmen"). Topic "§§ 88 ff." → "§ 96" (§ 88 = Rechtsstellung der Mitglieder, nicht Verfahren).
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-024",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "multi",
    topic: "PAO § 14 — Versagungsgründe",
    title: "Versagungsgründe der Zulassung (§ 14 PAO)",
    prompt:
      "Welche der folgenden Umstände können nach § 14 PAO zur Versagung der Zulassung zur Patentanwaltschaft führen? (Mehrere Antworten möglich)",
    options: [
      "Verurteilung wegen einer vorsätzlichen Straftat zu einer Freiheitsstrafe von mehr als einem Jahr",
      "Bestehende Vermögensverfallenheit (Insolvenz, geordnete Vermögensverhältnisse nicht gegeben)",
      "Fehlen eines abgeschlossenen Masterstudiums (Bachelor reicht aus)",
      "Unvereinbarkeit der angestrebten Tätigkeit als Patentanwalt mit dem bisherigen Beruf (z.B. aktiver Richter)",
    ],
    answers: [0, 1, 3],
    explanation:
      "§ 14 PAO enthält die Versagungsgründe der Zulassung: (1) Strafgerichtliche Verurteilung wegen Straftaten, die die Zulassung unvereinbar machen (insb. Verurteilung zu Freiheitsstrafe über einem Jahr bei vorsätzlichen Delikten); (2) in Vermögensverfall geratene Person (geordnete wirtschaftliche Verhältnisse nicht vorhanden, z.B. Insolvenz); (3) Unvereinbarkeit mit einem anderen Beruf (aktiver Richter, Beamter ohne Genehmigung). Ein Bachelor-Abschluss kann als Hochschulabschluss ausreichen, wenn er den fachlichen Anforderungen entspricht — ein Masterabschluss ist nicht zwingend vorgeschrieben (C ist falsch).",
    sourceIds: ["pao14"],
    sources: [
      { id: "pao14", label: "§ 14 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__14.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Subsumtion",
    skillTags: ["berufsrecht", "zulassung", "versagungsgruende"],
    // FIX (verifiziert): Versagung der Zulassung § 6 → § 14 PAO in topic, title, prompt,
    // explanation und source (pao6 → pao14).
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-025",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO — Interessenkonflikt (Fall)",
    title: "Fall: Widerstreitende Interessen — Gemeinschaftliche Anmeldung",
    prompt:
      "Patentanwalt P vertritt Unternehmen A und Unternehmen B gemeinsam bei einer gemeinschaftlichen Patentanmeldung. Im Laufe des Verfahrens geraten A und B in einen Streit über die Erfinderrechte. Darf P weiterhin beide vertreten?",
    options: [
      "Ja — da P beide Parteien bereits kennt, ist er am besten für eine einvernehmliche Lösung geeignet.",
      "Nein — sobald ein Interessenkonflikt zwischen den Mandanten entsteht, muss P das Mandat für beide beenden und darf keinem der beiden weiterhelfen.",
      "Ja — P darf weiterhin vertreten, muss aber beide Parteien über den Konflikt informieren.",
      "Nein — P darf den Streit zwischen A und B ohnehin nicht bearbeiten, kann aber die neutrale Patentanmeldung weiter betreuen.",
    ],
    answers: [1],
    explanation:
      "Das Verbot widerstreitender Interessen folgt aus § 39 Abs. 1 PAO (gewissenhafte Berufsausübung) i.V.m. dem allgemeinen berufsrechtlichen Grundsatz: Ein Patentanwalt darf keine Mandate übernehmen oder weiterführen, bei denen widerstreitende Interessen bestehen. Sobald A und B in einen echten Interessenkonflikt über die gemeinsame Anmeldung geraten (Streit über Erfinderrechte), kann P keinen der beiden weiter vertreten — weder im Streit noch in der Anmeldung, da diese untrennbar mit dem Streitgegenstand verbunden ist. P muss beide Mandate niederlegen.",
    sourceIds: ["pao39"],
    sources: [
      { id: "pao39", label: "§ 39 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__39.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["berufsrecht", "interessenkonflikt", "mandat"],
    // REVIEW da-pao-025 (REVIEWED): Verbot widerstreitender Interessen (§ 39 PAO i.V.m.
    // berufsordnungsrechtl. Grundsatz) korrekt angewandt — bei echtem Konflikt über die
    // gemeinsame Anmeldung muss P beide Mandate niederlegen; Lösung Index 1 eindeutig, Optionen
    // A/C/D klar falsch, 4 Optionen, kein Leak.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-026",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO §§ 5–7 — Ausbildung zum Patentanwalt",
    title: "Ausbildungszeit: Dauer und Stationen",
    prompt:
      "Welche Gesamtdauer der Ausbildungszeit ist nach § 7 PAO grundsätzlich vorgeschrieben, und wie verteilt sie sich auf die Stationen?",
    options: [
      "24 Monate insgesamt: 12 Monate bei einem Patentanwalt, 12 Monate beim DPMA",
      "34 Monate insgesamt: 26 Monate bei einem Patentanwalt oder Patentassessor, 2 Monate beim DPMA, 6 Monate beim Bundespatentgericht",
      "48 Monate insgesamt: 36 Monate bei einem Patentanwalt, 12 Monate Referendariat",
      "18 Monate insgesamt ausschließlich beim DPMA",
    ],
    answers: [1],
    explanation:
      "§ 7 PAO: Die Ausbildungszeit beträgt grundsätzlich 34 Monate. Sie gliedert sich in drei Stationen: mindestens 26 Monate bei einem Patentanwalt oder Patentassessor (praktische Ausbildung), mindestens 2 Monate beim Deutschen Patent- und Markenamt (DPMA) und mindestens 6 Monate beim Bundespatentgericht (BPatG). Die DPMA-Station dient dem Einblick in die Prüfungs- und Verwaltungsarbeit; die BPatG-Station vermittelt Erfahrung im gerichtlichen Verfahren. Auf die Ausbildungszeit können unter Umständen Zeiten anderer Tätigkeiten angerechnet werden (§ 7 PAO).",
    sourceIds: ["pao7"],
    sources: [
      { id: "pao7", label: "§ 7 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__7.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["berufsrecht", "ausbildung", "ausbildungszeit"],
    // FIX (verifiziert): Antwortoption Index 1 und Explanation korrigiert: "8 Monate DPMA" →
    // "2 Monate DPMA + 6 Monate BPatG" (§ 7 PAO). BPatG-Station war zuvor fehlend.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-027",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO — Berufsaufsicht (Fall)",
    title: "Fall: Schweigepflicht vs. Zeugenaussage",
    prompt:
      "Patentanwalt P wird von der Staatsanwaltschaft als Zeuge in einem Strafverfahren geladen. Es geht um eine Handlung, von der P ausschließlich im Rahmen eines Mandats erfahren hat. Muss P aussagen?",
    options: [
      "Ja — Staatsanwaltschaft und Gerichte haben Vorrang vor dem Mandatsgeheimnis.",
      "Nein — P hat ein gesetzliches Zeugnisverweigerungsrecht nach § 53 Abs. 1 Nr. 3 StPO, das aus der Verschwiegenheitspflicht folgt; er darf die Aussage verweigern.",
      "Nein — aber nur, wenn der Mandant ausdrücklich der Aussage zustimmt.",
      "Ja — das Zeugnisverweigerungsrecht gilt nur für Rechtsanwälte, nicht für Patentanwälte.",
    ],
    answers: [1],
    explanation:
      "§ 53 Abs. 1 Nr. 3 StPO: Patentanwälte sind zur Verweigerung des Zeugnisses über das berechtigt, was ihnen in ihrer Eigenschaft als Patentanwalt anvertraut oder bekanntgeworden ist. Dieses Zeugnisverweigerungsrecht korrespondiert mit der Verschwiegenheitspflicht aus § 39a PAO. P darf die Aussage verweigern — er ist dazu nicht nur berechtigt, sondern hat wegen § 39a PAO auch die Pflicht, die Vertraulichkeit zu wahren (sofern kein Entbindung durch den Mandanten erfolgt). Option C ist unvollständig: Das Verweigerungsrecht besteht unabhängig von einer Zustimmung des Mandanten, allerdings kann der Mandant den Patentanwalt von der Schweigepflicht entbinden.",
    sourceIds: ["pao39a"],
    sources: [
      { id: "pao39a", label: "§ 39a PAO", url: "https://www.gesetze-im-internet.de/patanwo/__39a.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Rechtsfolge",
    skillTags: ["berufsrecht", "verschwiegenheit", "zeugnisverweigerung"],
    // REVIEW da-pao-027 (REVIEWED): § 53 Abs. 1 Nr. 3 StPO gewährt Patentanwälten ein
    // Zeugnisverweigerungsrecht, korrespondierend mit § 39a PAO — korrekt zitiert. Lösung Index 1
    // eindeutig richtig; Option C (nur bei Zustimmung) zu Recht als unvollständig markiert;
    // 4 Optionen, kein Leak. Hinweis: Hauptquelle ist hier StPO/§ 39a PAO — sourceId pao39a ok.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-028",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO § 56 — Aufgaben der Kammer",
    title: "Aufgaben des Vorstands der Patentanwaltskammer",
    prompt:
      "Welche der folgenden Aufgaben gehört zum Aufgabenkreis des Vorstands der Patentanwaltskammer nach § 61 ff. PAO?",
    options: [
      "Erlass von Gesetzen zum gewerblichen Rechtsschutz",
      "Entscheidung über die Zulassung zur Patentanwaltsprüfung (liegt beim Patentamt/Justizministerium)",
      "Führung der Liste der Patentanwälte und Ausübung der Berufsaufsicht",
      "Vergabe von Patenten an Mitglieder der Kammer",
    ],
    answers: [2],
    explanation:
      "§§ 61 ff. PAO: Der Vorstand der Patentanwaltskammer vertritt die Kammer nach außen, führt die Liste der zugelassenen Patentanwälte und Patentanwaltsgesellschaften, übt die Berufsaufsicht aus (inkl. Rügebefugnis, § 70 PAO) und erledigt die laufenden Angelegenheiten der Kammer. Er erlässt keine Gesetze (das ist dem Gesetzgeber vorbehalten) und vergibt keine Patente (Aufgabe des DPMA). Über die Zulassung zur Patentanwaltsprüfung entscheidet das DPMA/Justizministerium nach den §§ 8 ff. PAO.",
    sourceIds: ["pao61", "pao70"],
    sources: [
      { id: "pao61", label: "§ 61 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__61.html" },
      { id: "pao70", label: "§ 70 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__70.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["berufsrecht", "patentanwaltskammer", "vorstand"],
    // FIX (verifiziert): Rügebefugnis § 44 → § 70 PAO in der explanation; source pao70 ergänzt.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-029",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO — Sortenschutz (Fall)",
    title: "Fall: Sortenrecht im Tätigkeitsbereich des Patentanwalts",
    prompt:
      "Landwirtschaftliches Unternehmen L entwickelt eine neue Pflanzensorte und fragt Patentanwalt P, ob P sie bei der Anmeldung eines Sortenschutzes nach dem Sortenschutzgesetz (SortSchG) vertreten kann. Ist P dazu nach § 3 PAO befugt?",
    options: [
      "Nein — Sortenschutz ist kein gewerbliches Schutzrecht und liegt außerhalb des Tätigkeitsbereichs des Patentanwalts.",
      "Ja — Sortenschutzrechte sind ausdrücklich in § 3 Abs. 2 PAO als Tätigkeitsbereich des Patentanwalts genannt.",
      "Ja — aber nur, wenn P zusätzlich als Rechtsanwalt zugelassen ist.",
      "Nein — für Sortenschutz ist ausschließlich das Bundessortenamt zuständig, nicht Patentanwälte.",
    ],
    answers: [1],
    explanation:
      "§ 3 Abs. 2 PAO: Sortenschutzrechte sind ausdrücklich im enumerativen Tätigkeitsbereich des Patentanwalts aufgeführt. P ist damit befugt, L bei der Anmeldung eines Sortenschutzes nach dem SortSchG zu vertreten und zu beraten. Dies ist ein prüfungsrelevantes Detail, das häufig vergessen wird — der Sortenschutz ist kein Patentrecht im technischen Sinne, aber ausdrücklich in den PAO-Tätigkeitsbereich einbezogen.",
    sourceIds: ["pao3"],
    sources: [
      { id: "pao3", label: "§ 3 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__3.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["berufsrecht", "taetigkeitsbereich", "sortenschutz"],
    // FIX (verifiziert): § 3 Abs. 1 → § 3 Abs. 2 PAO in Option und Explanation (Enumeration steht
    // in Abs. 2). Sortenschutz korrekt als in § 3 Abs. 2 PAO enthalten belassen (autorisierte
    // §-Tabelle bestätigt: § 3 Abs. 2 ENTHÄLT Sortenschutz).
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-030",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "PAO § 39 — Fortbildung",
    title: "Fortbildungspflicht des Patentanwalts",
    prompt:
      "Was schreibt § 39 PAO bezüglich der beruflichen Fortbildung vor?",
    options: [
      "Eine Fortbildungspflicht ist in der PAO nicht geregelt; Fortbildung ist rein freiwillig.",
      "Der Patentanwalt ist verpflichtet, mindestens 30 Stunden Fortbildung pro Jahr nachzuweisen.",
      "Der Patentanwalt ist verpflichtet, sich in den für seine Berufsausübung erforderlichen Bereichen fortzubilden; die konkreten Anforderungen werden durch die Berufsordnung der Kammer konkretisiert.",
      "Die Fortbildungspflicht gilt nur für Patentanwälte, die weniger als fünf Jahre zugelassen sind.",
    ],
    answers: [2],
    explanation:
      "§ 39 Abs. 3 PAO: Der Patentanwalt ist verpflichtet, sich beruflich fortzubilden und die für seine Berufsausübung erforderlichen Kenntnisse zu erhalten und weiterzuentwickeln. Die konkreten Anforderungen (Stundenzahl, Nachweismodalitäten) werden durch die Berufsordnung der Patentanwaltskammer geregelt. Es gibt keine starre 30-Stunden-Pflicht im Gesetzestext; die Pflicht gilt für alle zugelassenen Patentanwälte unabhängig von der Zulassungsdauer.",
    sourceIds: ["pao39"],
    sources: [
      { id: "pao39", label: "§ 39 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__39.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["berufsrecht", "fortbildung", "berufspflichten"],
    // REVIEW da-pao-030 (REVIEWED): Fortbildungspflicht des Patentanwalts (§ 39 PAO, durch
    // Berufsordnung konkretisiert; keine starre 30-Std.-Regel im Gesetz; gilt für alle
    // Zugelassenen) — Lösung Index 2 korrekt, Distraktoren klar falsch, 4 Optionen, kein Leak.
    // Hinweis Autor: exakte Verortung "§ 39 Abs. 3 PAO" am Wortlaut gegenchecken (Kern § 39 PAO
    // unstrittig).
    reviewStatus: "reviewed",
  },

  // ─── ORDERING da-pao-031 … da-pao-034 ────────────────────────────────────

  {
    id: "da-pao-031",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "PAO §§ 7, 14, 18, 19 — Weg zur Zulassung",
    title: "Prüfungsschema: Weg zur Zulassung als Patentanwalt",
    prompt:
      "Bringen Sie die folgenden Schritte auf dem Weg zur Zulassung als Patentanwalt in die gesetzlich vorgesehene Reihenfolge.",
    options: [
      "Abgeschlossene Ausbildungszeit (§ 7 PAO): mind. 26 Monate Kanzlei/Patentassessor + 2 Monate DPMA + 6 Monate BPatG",
      "Bestandene Patentanwaltsprüfung (§§ 8 ff. PAO)",
      "Antrag auf Zulassung bei der Patentanwaltskammer; Prüfung der Versagungsgründe (§ 14 PAO)",
      "Vereidigung (§ 19 PAO)",
      "Wirksamwerden der Zulassung mit Aushändigung der Zulassungsurkunde durch die Patentanwaltskammer (§ 18 PAO)",
    ],
    answers: [0, 1, 2, 3, 4],
    orderItems: [
      "Abgeschlossene Ausbildungszeit (§ 7 PAO): mind. 26 Monate Kanzlei/Patentassessor + 2 Monate DPMA + 6 Monate BPatG",
      "Bestandene Patentanwaltsprüfung (§§ 8 ff. PAO)",
      "Antrag auf Zulassung bei der Patentanwaltskammer; Prüfung der Versagungsgründe (§ 14 PAO)",
      "Vereidigung (§ 19 PAO)",
      "Wirksamwerden der Zulassung mit Aushändigung der Zulassungsurkunde durch die Patentanwaltskammer (§ 18 PAO)",
    ],
    explanation:
      "Der Zulassungsweg zum Patentanwalt verläuft in dieser Reihenfolge: (1) Ausbildungszeit ableisten (§ 7 PAO — 34 Monate gesamt: 26 Mon Kanzlei/Patentassessor + 2 Mon DPMA + 6 Mon BPatG); (2) Patentanwaltsprüfung bestehen (§§ 8 ff. PAO — schriftlich und mündlich); (3) Zulassungsantrag bei der Patentanwaltskammer stellen, die das Vorliegen aller Voraussetzungen und das Fehlen von Versagungsgründen (§ 14 PAO) prüft; (4) Vereidigung (§ 19 PAO); (5) die Zulassung wird wirksam mit der Aushändigung der Zulassungsurkunde durch die Patentanwaltskammer (§ 18 PAO) — damit wird der Bewerber Mitglied der Kammer und darf die Berufsbezeichnung 'Patentanwalt' führen. Hinweis: § 23 PAO ist weggefallen.",
    sourceIds: ["pao7", "pao14", "pao19", "pao18"],
    sources: [
      { id: "pao7", label: "§ 7 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__7.html" },
      { id: "pao14", label: "§ 14 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__14.html" },
      { id: "pao19", label: "§ 19 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__19.html" },
      { id: "pao18", label: "§ 18 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__18.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["berufsrecht", "zulassung", "pruefungsschema"],
    // FIX (Orchestrator, WebFetch verifiziert): § 23 PAO ist WEGGEFALLEN → "Eintragung § 23" entfernt.
    // Sequenz auf verifizierte §§ umgebaut: Ausbildung § 7 (26+2+6) → Prüfung §§ 8 ff. → Antrag/
    // Versagungsprüfung § 14 → Vereidigung § 19 → Zulassung wirksam mit Urkunde § 18 (Vereidigung ist
    // gesetzliche Voraussetzung der Urkundenaushändigung, § 18 PAO).
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-032",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "PAO § 70 / § 96 — Berufsaufsicht: Eskalationsstufen",
    title: "Prüfungsschema: Eskalationsstufen der Berufsaufsicht",
    prompt:
      "Ordnen Sie die Instrumente der Berufsaufsicht nach aufsteigender Schwere der Maßnahme.",
    options: [
      "Informelles Gespräch / Beratung durch den Vorstand (keine Maßnahme i.e.S.)",
      "Rüge (§ 70 PAO) — schriftliche Missbilligung durch den Kammervorstand",
      "Berufsgerichtliches Verfahren — Warnung oder Verweis (§ 96 Nr. 1/2 PAO)",
      "Berufsgerichtliches Verfahren — Geldbuße bis 25.000 € (§ 96 Nr. 3 PAO)",
      "Berufsgerichtliches Verfahren — Ausschließung aus der Patentanwaltschaft (§ 96 Nr. 5 PAO)",
    ],
    answers: [0, 1, 2, 3, 4],
    orderItems: [
      "Informelles Gespräch / Beratung durch den Vorstand (keine Maßnahme i.e.S.)",
      "Rüge (§ 70 PAO) — schriftliche Missbilligung durch den Kammervorstand",
      "Berufsgerichtliches Verfahren — Warnung oder Verweis (§ 96 Nr. 1/2 PAO)",
      "Berufsgerichtliches Verfahren — Geldbuße bis 25.000 € (§ 96 Nr. 3 PAO)",
      "Berufsgerichtliches Verfahren — Ausschließung aus der Patentanwaltschaft (§ 96 Nr. 5 PAO)",
    ],
    explanation:
      "Die Eskalationsstufen der Berufsaufsicht verlaufen von mild nach schwer: Zunächst informelle Beratung (kein Zwang), dann die Rüge als formelle schriftliche Missbilligung durch den Kammervorstand (§ 70 PAO, bei geringen Verstößen), dann das berufsgerichtliche Verfahren mit gestuften Maßnahmen (§ 96 PAO): Warnung/Verweis als mildeste Sanktionen, Geldbuße als mittlere, Tätigkeitsverbot als schwere und schließlich die Ausschließung als schärfste Maßnahme. Jede Stufe setzt schwerere oder hartnäckigere Verstöße voraus.",
    sourceIds: ["pao70", "pao96"],
    sources: [
      { id: "pao70", label: "§ 70 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__70.html" },
      { id: "pao96", label: "§ 96 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__96.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["berufsrecht", "berufsaufsicht", "massnahmen", "pruefungsschema"],
    // FIX (verifiziert): Rüge § 44 → § 70 PAO in topic, options, orderItems und explanation;
    // "anwaltsgerichtlich" → "berufsgerichtlich"; source pao44 → pao70.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-033",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "PAO — Prüfungsschema: Interessenkonflikt-Prüfung",
    title: "Prüfungsschema: Zulässigkeit eines neuen Mandats bei möglichem Interessenkonflikt",
    prompt:
      "In welcher Reihenfolge prüft der Patentanwalt, ob er ein neues Mandat annehmen darf, wenn ein möglicher Interessenkonflikt besteht?",
    options: [
      "Identifizierung aller betroffenen Parteien und Sachverhalte",
      "Prüfung: Besteht ein bestehendes oder früheres Mandatsverhältnis mit einer Gegenpartei?",
      "Prüfung: Sind Kenntnisse aus früherem Mandat (§ 39a PAO) betroffen?",
      "Prüfung: Liegen widerstreitende Interessen vor (§ 39 PAO)?",
      "Entscheidung: Mandat annehmen (kein Konflikt) oder ablehnen / niederlegen (Konflikt besteht)",
    ],
    answers: [0, 1, 2, 3, 4],
    orderItems: [
      "Identifizierung aller betroffenen Parteien und Sachverhalte",
      "Prüfung: Besteht ein bestehendes oder früheres Mandatsverhältnis mit einer Gegenpartei?",
      "Prüfung: Sind Kenntnisse aus früherem Mandat (§ 39a PAO) betroffen?",
      "Prüfung: Liegen widerstreitende Interessen vor (§ 39 PAO)?",
      "Entscheidung: Mandat annehmen (kein Konflikt) oder ablehnen / niederlegen (Konflikt besteht)",
    ],
    explanation:
      "Die Interessenkonflikt-Prüfung verläuft strukturiert: Zunächst werden Parteien und Sachverhalt identifiziert, dann geprüft, ob ein bestehendes oder früheres Mandatsverhältnis zu einer Gegenpartei besteht (§ 39 PAO). Falls ja, folgt die Prüfung, ob Kenntnisse aus dem früheren Mandat (Verschwiegenheitsrelevanz nach § 39a PAO) betroffen sind. Abschließend wird geprüft, ob widerstreitende Interessen vorliegen — bejahendenfalls ist das Mandat abzulehnen oder niederzulegen. Diese Struktur entspricht dem berufsrechtlichen Pflichtenkatalog.",
    sourceIds: ["pao39", "pao39a"],
    sources: [
      { id: "pao39", label: "§ 39 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__39.html" },
      { id: "pao39a", label: "§ 39a PAO", url: "https://www.gesetze-im-internet.de/patanwo/__39a.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["berufsrecht", "interessenkonflikt", "pruefungsschema"],
    // REVIEW da-pao-033 (REVIEWED): Interessenkonflikt-Prüfschema — §-Anker korrekt (§ 39a PAO
    // Verschwiegenheits-Kenntnisse, § 39 PAO widerstreitende Interessen); Reihenfolge
    // (Parteien identifizieren → früheres Mandat? → Kenntnisbetroffenheit? → widerstreitende
    // Interessen? → Entscheidung) didaktisch eindeutig. Keine falschen §-Nummern, kein Leak.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-034",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "PAO — Topographieschutz als Tätigkeitsbereich (Fall-Struktur)",
    title: "Fall: Patentanwalt und Halbleitertopographie — Prüfungsschritte",
    prompt:
      "Mandant H fragt Patentanwalt P, ob P ihn bei der Anmeldung einer Halbleitertopographie (Chip-Design) vertreten kann. Ordnen Sie die Prüfungsschritte in der richtigen Reihenfolge.",
    options: [
      "Prüfung: Fällt Halbleitertopographieschutz unter § 3 Abs. 2 PAO?",
      "Feststellung: Ja — § 3 Abs. 2 PAO nennt 'Topographien von Halbleitererzeugnissen' ausdrücklich",
      "Prüfung: Liegt ein Versagungsgrund vor (Interessenkonflikt, fehlende Sachkenntnis)?",
      "Entscheidung: Mandat annehmen und H bei der Anmeldung nach dem Halbleiterschutzgesetz vertreten",
    ],
    answers: [0, 1, 2, 3],
    orderItems: [
      "Prüfung: Fällt Halbleitertopographieschutz unter § 3 Abs. 2 PAO?",
      "Feststellung: Ja — § 3 Abs. 2 PAO nennt 'Topographien von Halbleitererzeugnissen' ausdrücklich",
      "Prüfung: Liegt ein Versagungsgrund vor (Interessenkonflikt, fehlende Sachkenntnis)?",
      "Entscheidung: Mandat annehmen und H bei der Anmeldung nach dem Halbleiterschutzgesetz vertreten",
    ],
    explanation:
      "Die Prüfung der Zulässigkeit eines Mandats beginnt mit der Frage, ob das Schutzrecht in den Tätigkeitsbereich nach § 3 PAO fällt. Für Halbleitertopographien ist dies ausdrücklich zu bejahen — § 3 Abs. 2 PAO nennt 'Topographien von Halbleitererzeugnissen' als einen der aufgezählten Schutzbereiche. Danach folgt die Interessenkonflikt- und Sachkunde-Prüfung. Fehlt beides, kann P das Mandat annehmen und H nach dem Halbleiterschutzgesetz (HalbSchG) vertreten.",
    sourceIds: ["pao3"],
    sources: [
      { id: "pao3", label: "§ 3 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__3.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["berufsrecht", "taetigkeitsbereich", "topographie", "pruefungsschema"],
    // FIX (verifiziert): § 3 Abs. 1 → § 3 Abs. 2 PAO an allen 3 Stellen (options[0], options[1],
    // orderItems[0], orderItems[1], explanation). Enumeration steht in Abs. 2, nicht Abs. 1.
    reviewStatus: "reviewed",
  },

  // ─── CLOZE da-pao-035 … da-pao-038 ───────────────────────────────────────

  {
    id: "da-pao-035",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "PAO § 39a — Verschwiegenheitspflicht",
    title: "Lückentext: Verschwiegenheitspflicht — Paragraph",
    prompt:
      "Der Patentanwalt ist nach ___ PAO zur Verschwiegenheit über alles verpflichtet, was ihm in Ausübung seines Berufs bekannt geworden ist. Er hat auch die bei seiner Berufsausübung beschäftigten Personen zur Verschwiegenheit zu verpflichten.",
    options: [
      "§ 39a",
      "§ 39",
      "§ 26",
      "§ 53",
    ],
    answers: [0],
    explanation:
      "§ 39a PAO regelt ausdrücklich die Verschwiegenheitspflicht des Patentanwalts und die Pflicht zur Verpflichtung der Mitarbeiter. § 39 PAO enthält die allgemeine Berufspflicht (gewissenhafte Berufsausübung), § 26 PAO die Kanzleipflicht, § 53 PAO die Rechtsstellung der Patentanwaltskammer.",
    sourceIds: ["pao39a"],
    sources: [
      { id: "pao39a", label: "§ 39a PAO", url: "https://www.gesetze-im-internet.de/patanwo/__39a.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["berufsrecht", "verschwiegenheit", "paragraph-kenntnis"],
    // REVIEW da-pao-035 (REVIEWED): Cloze Verschwiegenheit = § 39a PAO — Lücke korrekt (Index 0),
    // genau ein "___", Distraktoren (§ 39/§ 26/§ 53) sauber abgegrenzt, explanation richtig,
    // kein Leak.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-036",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "PAO § 26 — Kanzleipflicht",
    title: "Lückentext: Kanzleipflicht — Paragraph",
    prompt:
      "Die Pflicht des Patentanwalts, im Inland eine Kanzlei einzurichten und zu unterhalten, ergibt sich aus ___ PAO.",
    options: [
      "§ 26",
      "§ 39",
      "§ 5",
      "§ 44",
    ],
    answers: [0],
    explanation:
      "§ 26 PAO regelt die Kanzleipflicht (Niederlassungspflicht). § 39 PAO enthält die allgemeinen Berufspflichten (gewissenhafte Berufsausübung), § 5 PAO die Zulassungsvoraussetzungen. § 44 PAO (Distraktor) ist nicht die Norm für die Rüge — die Rüge durch den Kammervorstand ergibt sich aus § 70 PAO.",
    sourceIds: ["pao26"],
    sources: [
      { id: "pao26", label: "§ 26 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__26.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["berufsrecht", "kanzleipflicht", "paragraph-kenntnis"],
    // FIX (verifiziert): Explanation korrigiert: "§ 44 PAO die Rüge" → Klarstellung dass
    // § 44 kein Rüge-§ ist, sondern die Rüge unter § 70 PAO steht. Distraktor-Option § 44
    // bleibt unverändert.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-037",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "PAO § 53 — Patentanwaltskammer",
    title: "Lückentext: Rechtsnatur der Patentanwaltskammer",
    prompt:
      "Die Patentanwaltskammer ist nach § 53 PAO eine ___ des öffentlichen Rechts mit dem Recht zur Selbstverwaltung.",
    options: [
      "Körperschaft",
      "Anstalt",
      "Stiftung",
      "Behörde",
    ],
    answers: [0],
    explanation:
      "§ 53 Abs. 1 PAO: Die Patentanwaltskammer ist eine Körperschaft des öffentlichen Rechts. Anstalten und Stiftungen sind andere Organisationsformen des öffentlichen Rechts. Behörden sind staatliche Stellen — die Kammer ist eine Selbstverwaltungskörperschaft, keine staatliche Behörde.",
    sourceIds: ["pao53"],
    sources: [
      { id: "pao53", label: "§ 53 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__53.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["berufsrecht", "patentanwaltskammer", "rechtsnatur"],
    // REVIEW da-pao-037 (REVIEWED): Cloze Rechtsnatur Kammer = "Körperschaft" des öff. Rechts
    // (§ 53 PAO) — Lücke korrekt (Index 0), genau ein "___", Distraktoren (Anstalt/Stiftung/
    // Behörde) sauber, explanation richtig, kein Leak.
    reviewStatus: "reviewed",
  },

  {
    id: "da-pao-038",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "PAO § 3 — Tätigkeitsbereich: Sondergebiete",
    title: "Lückentext: Überraschungsgebiete im Tätigkeitsbereich § 3 PAO",
    prompt:
      "Neben Patenten, Gebrauchsmustern und Marken nennt § 3 Abs. 2 PAO als Tätigkeitsbereich des Patentanwalts auch ___ und Sortenschutzrechte.",
    options: [
      "Topographien von Halbleitererzeugnissen",
      "Urheberrechte",
      "Wettbewerbsrecht (UWG)",
      "Lizenzvertragsrecht",
    ],
    answers: [0],
    explanation:
      "§ 3 Abs. 2 PAO zählt ausdrücklich 'Topographien von Halbleitererzeugnissen' (Halbleiterschutz) und 'Sortenschutzrechte' als Tätigkeitsgebiete des Patentanwalts auf. Urheberrecht, Wettbewerbsrecht (UWG) und allgemeines Lizenzvertragsrecht sind nicht aufgezählt — für diese ist grundsätzlich der Rechtsanwalt zuständig, es sei denn, sie stehen im unmittelbaren Zusammenhang mit gewerblichem Rechtsschutz.",
    sourceIds: ["pao3"],
    sources: [
      { id: "pao3", label: "§ 3 PAO", url: "https://www.gesetze-im-internet.de/patanwo/__3.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["berufsrecht", "taetigkeitsbereich", "topographie", "sortenschutz"],
    // FIX (verifiziert): § 3 Abs. 1 → § 3 Abs. 2 PAO in prompt und explanation. Sortenschutz
    // korrekt als in § 3 Abs. 2 PAO enthalten belassen (autorisierte §-Tabelle bestätigt:
    // § 3 Abs. 2 ENTHÄLT Sortenschutz UND Topographien). Cloze-Lösung "Topographien von
    // Halbleitererzeugnissen" (Index 0) bleibt korrekt.
    reviewStatus: "reviewed",
  },
];
